import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { I18n } from '@aws-amplify/core'; 
import { translations } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import awsconfig from '../aws-exports';
import { generateClient } from 'aws-amplify/api';
import { post } from 'aws-amplify/api';
import { createMeal } from '../graphql/mutations'; 
import { listMeals, getUserCharacter } from '../graphql/queries';
import { CHARACTERS, getCharacterById, getExpressionImage } from '../constants/characters';     

const client = generateClient();

// Lambda経由でGemini APIを呼び出す
async function callGemini(prompt, imageBase64 = null, mimeType = null) {
  const body = { prompt, ...(imageBase64 ? { imageBase64, mimeType } : {}) };
  const restOperation = post({
    apiName: 'yukikenApi',
    path: '/gemini',
    options: { body }
  });
  const response = await restOperation.response;
  const data = await response.body.json();
  if (!data.text) throw new Error('Gemini APIからの応答が空です');
  return data.text;
}

I18n.putVocabularies(translations);
I18n.setLanguage('ja');
Amplify.configure(awsconfig);

export default function Home({ user, signOut }) {
  const [attributes, setAttributes] = useState({});
  const [response, setResponse] = useState("画像をアップロードしてね！");
  const [mainData, setMainData] = useState(null);
  const [vitaminData, setVitaminData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [score, setScore] = useState(null);
  const [charImage, setCharImage] = useState(null);
  const [mealType, setMealType] = useState('朝食');
  const [activeChar, setActiveChar] = useState(getCharacterById('yukito'));
  const [shortComment, setShortComment] = useState('');

  // ユーザー属性 & 選択キャラをDynamoDBから取得
  useEffect(() => {
    async function getUserData() {
      try {
        const attrs = await fetchUserAttributes();
        setAttributes(attrs);
        const uid = attrs.sub;
        const result = await client.graphql({
          query: getUserCharacter,
          variables: { id: uid },
        });
        const record = result.data.getUserCharacter;
        if (record) {
          const char = getCharacterById(record.characterId);
          setActiveChar(char);
          setCharImage(char.defaultImage);
        } else {
          setCharImage(getCharacterById('yukito').defaultImage);
        }
      } catch (err) {
        console.error("初期化エラー:", err);
        setCharImage(CHARACTERS.yukito.defaultImage);
      }
    }
    getUserData();
  }, []);

  // 取得した属性からニックネームを決定
  const userNickname = attributes.nickname || attributes['custom:nickname'] || user?.username || "ゲスト";
  const userGender = attributes['gender'] || '未回答';

  const getTodayString = () => new Date().toLocaleDateString('sv-SE');





  async function analyzeImages(event) {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setScore(null); setMainData(null); setVitaminData(null);
    setPreviewUrls(files.map(file => URL.createObjectURL(file)));
    setLoading(true);
    setResponse(`${mealType}を精密分析中。少々お待ちください。`);

    try {
      const genderBaseValues = userGender === '女性' 
        ? "【1日基準】P:50g, F:50g, C:230g, 鉄:10.5mg, 塩:6.5g, A:700μg, B1:1.1mg, B2:1.2mg, C:100mg, D:8.5μg"
        : "【1日基準】P:66g, F:60g, C:270g, 鉄:7.5mg, 塩:7.5g, A:900μg, B1:1.5mg, B2:1.5mg, C:105mg, D:9.0μg";
      // 画像をbase64に変換（最初の1枚のみ送信）
      const firstFile = files[0];
      const imageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(firstFile);
      });
      const mimeType = firstFile.type;

      const prompt = `以下の指示に従い、純粋なJSONオブジェクトのみを返してください。マークダウン、コードブロック、説明文は一切不要です。JSONの外側に文字を書かないでください。
あなたは${activeChar.personality}。画像を見て以下のJSONを出力してください。
      ユーザーのニックネーム：${userNickname}
      性別：${userGender}
      分析対象：${mealType}（1食分）
      
      ${genderBaseValues}
      上記基準の「1/3（約33%）」を今回の目標100.0%として算出してください。
      アドバイスでは、たまに「${userNickname}」と名前を呼んであげて。

      {
        "score": 採点(0-100),
        "shortComment": "分析結果の短評。キャラらしい一言（30字以内）",
        "report": "料理の内容、ボリューム感、隠れた食材の推測を含む栄養学的な具体的で少し厳しめ、かつ愛のあるアドバイス",
        "mainData": {
          "タンパク質": {"rate": (1食目標に対する%), "amount": "数値", "unit": "g"},
          "脂質": {"rate": (1食目標に対する%), "amount": "数値", "unit": "g"},
          "炭水化物": {"rate": (1食目標に対する%), "amount": "数値", "unit": "g"},
          "鉄分": {"rate": (1食目標に対する%), "amount": "数値", "unit": "mg"},
          "塩分": {"rate": (1食目標に対する%), "amount": "数値", "unit": "g"}
        },
        "vitaminData": {
          "A": {"rate": %, "amount": "数値", "unit": "μg"},
          "B1": {"rate": %, "amount": "数値", "unit": "mg"},
          "B2": {"rate": %, "amount": "数値", "unit": "mg"},
          "C": {"rate": %, "amount": "数値", "unit": "mg"},
          "D": {"rate": %, "amount": "数値", "unit": "μg"}
        }
      }`;

      const responseText = await callGemini(prompt, imageBase64, mimeType);
      const cleanText1 = responseText.replace(/```json|```/g, '').trim();
      const jsonMatch = cleanText1.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('JSONが見つかりませんでした: ' + cleanText1);
      const data = JSON.parse(jsonMatch[0]);

      await client.graphql({
        query: createMeal,
        variables: {
          input: {
            date: getTodayString(),
            timing: mealType,
            protein: data.mainData["タンパク質"].rate,
            fat: data.mainData["脂質"].rate,
            carbs: data.mainData["炭水化物"].rate,
            salt: data.mainData["塩分"].rate,
            iron: data.mainData["鉄分"].rate,
            calories: 0, // 必要に応じてGeminiから取得するよう後日検討
            vitaminA: data.vitaminData["A"].rate,
            vitaminB1: data.vitaminData["B1"].rate,
            vitaminB2: data.vitaminData["B2"].rate,
            vitaminC: data.vitaminData["C"].rate,
            vitaminD: data.vitaminData["D"].rate,
            comment: data.report
          }
        }
      });

      setResponse(data.report);
      setShortComment(data.shortComment || '');
      setScore(data.score);
      setCharImage(getExpressionImage(activeChar, data.score));
      setMainData(Object.entries(data.mainData).map(([k, v]) => ({ label: k, value: v.rate, amount: v.amount, unit: v.unit })));
      setVitaminData(Object.entries(data.vitaminData).map(([k, v]) => ({ label: `ビタミン${k}`, value: v.rate, amount: v.amount, unit: v.unit })));

    } catch (err) {
      setResponse("解析中にエラーが発生しました。");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function getDailySummary() {
    setLoading(true);
    setResponse("今日の食事を集計中です...");
    try {
      const today = getTodayString();
      const { data: dbData } = await client.graphql({
        query: listMeals,
        variables: { filter: { date: { eq: today } } }
      });

      const meals = dbData.listMeals.items;
      if (meals.length === 0) {
        setResponse("今日のデータがまだ登録されておりません。");
        setLoading(false);
        return;
      }

      const sum = (key) => meals.reduce((acc, m) => acc + (m[key] || 0), 0) / 3;

      const totalResult = {
        protein: sum('protein'), fat: sum('fat'), carbs: sum('carbs'),
        salt: sum('salt'), iron: sum('iron'),
        vA: sum('vitaminA'), vB1: sum('vitaminB1'), vB2: sum('vitaminB2'), vC: sum('vitaminC'), vD: sum('vitaminD')
      };

      const summaryPrompt = `以下の指示に従い、純粋なJSONオブジェクトのみを返してください。マークダウン、コードブロック、説明文は一切不要です。JSONの外側に文字を書かないでください。
あなたは${activeChar.personality}。1日の食事集計データ（${JSON.stringify(totalResult)} ※数値は1日の目標100%に対する達成度）を見て、${userNickname}に栄養学的な愛のある総評をして。JSON形式 {"score": 採点, "shortComment": "総評の一言（30字以内）", "report": "総評"} で返して。`;
      
      const summaryText = await callGemini(summaryPrompt);
      const cleanText2 = summaryText.replace(/```json|```/g, '').trim();
      const summaryMatch = cleanText2.match(/\{[\s\S]*\}/);
      if (!summaryMatch) throw new Error('JSONが見つかりませんでした: ' + cleanText2);
      const resJson = JSON.parse(summaryMatch[0]);

      setResponse(resJson.report);
      setShortComment(resJson.shortComment || '');
      setScore(resJson.score);
      setCharImage(getExpressionImage(activeChar, resJson.score));

      setMainData([
        { label: "タンパク質", value: Math.round(totalResult.protein), amount: "-", unit: "%" },
        { label: "脂質", value: Math.round(totalResult.fat), amount: "-", unit: "%" },
        { label: "炭水化物", value: Math.round(totalResult.carbs), amount: "-", unit: "%" },
        { label: "鉄分", value: Math.round(totalResult.iron), amount: "-", unit: "%" },
        { label: "塩分", value: Math.round(totalResult.salt), amount: "-", unit: "%" }
      ]);
      setVitaminData([
        { label: "ビタミンA", value: Math.round(totalResult.vA), amount: "-", unit: "%" },
        { label: "ビタミンB1", value: Math.round(totalResult.vB1), amount: "-", unit: "%" },
        { label: "ビタミンB2", value: Math.round(totalResult.vB2), amount: "-", unit: "%" },
        { label: "ビタミンC", value: Math.round(totalResult.vC), amount: "-", unit: "%" },
        { label: "ビタミンD", value: Math.round(totalResult.vD), amount: "-", unit: "%" }
      ]);

    } catch (err) {
      setResponse("総評中にエラーが発生しました。");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const RenderBar = ({ label, value, amount, unit, color }) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold' }}>
        <span>{label} {amount !== "-" && `(${amount}${unit})`}</span>
        <span>{value}%</span>
      </div>
      <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '10px', height: '8px', overflow: 'hidden', marginTop: '3px' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color, height: '100%', transition: 'width 1s' }} />
      </div>
    </div>
  );

  return (
    <div style={{ padding: '80px 20px', backgroundColor: '#f4f7f9', minHeight: '100vh' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        
        {/* ヘッダー：ログアウトボタンを削除 */}
        <div style={{ marginBottom: '15px', padding: '12px', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
          <span style={{ fontWeight: 'bold' }}>👤 会員名: {userNickname} 様</span>
        </div>

        <h1 style={{ textAlign: 'center', color: '#2c3e50', borderBottom: '3px solid #e74c3c', paddingBottom: '8px', fontSize: '20px' }}>🥗 ゆきけん～白峰 雪人の栄養分析研究所～</h1>
        
        {/* 食事選択ボタン：朝・昼・晩のみ */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
          {['朝食', '昼食', '夕食'].map(type => (
            <button 
              key={type}
              onClick={() => setMealType(type)}
              style={{ flex: 1, padding: '12px 0', borderRadius: '12px', border: 'none', fontWeight: 'bold', backgroundColor: mealType === type ? '#e74c3c' : '#bdc3c7', color: '#fff', cursor: 'pointer' }}
            >
              {type}
            </button>
          ))}
        </div>

        <button 
          onClick={getDailySummary}
          disabled={loading}
          style={{ width: '100%', marginBottom: '20px', padding: '15px', background: '#2c3e50', color: '#fff', borderRadius: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
        >
          🌙 今日の総評を確認
        </button>

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <input type="file" multiple onChange={analyzeImages} disabled={loading} accept="image/*" id="upload" style={{ display: 'none' }} />
          <label htmlFor="upload" style={{ display: 'block', padding: '20px', background: '#e74c3c', color: '#fff', borderRadius: '40px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 4px 10px rgba(231, 76, 60, 0.3)' }}>
            {loading ? "分析中..." : `📷 ${mealType}を提出`}
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {previewUrls.length > 0 && (
            <div style={{ background: '#fff', padding: '15px', borderRadius: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto' }}>
                {previewUrls.map((url, i) => <img key={i} src={url} style={{ height: '100px', width: '100px', borderRadius: '10px', objectFit: 'cover' }} alt="meal" />)}
              </div>
            </div>
          )}

          <div style={{ background: '#fff', padding: '20px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <h3 style={{ color: '#2980b9', fontSize: '18px', marginBottom: '15px' }}>📊 栄養素分析</h3>
            {mainData ? (
              <>
                {mainData.map(d => <RenderBar key={d.label} {...d} color="#2980b9" />)}
                <div style={{ margin: '15px 0', borderTop: '1px dashed #ddd' }} />
                {vitaminData.map(d => <RenderBar key={d.label} {...d} color="#27ae60" />)}
              </>
            ) : <p style={{ textAlign: 'center', color: '#95a5a6' }}>データがここに表示されます。</p>}
          </div>

          <div style={{ background: '#fff', padding: '20px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            {/* キャラ＋吹き出しエリア */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', marginBottom: '16px' }}>
              {/* 左：キャラ画像 */}
              <div style={{ flexShrink: 0, textAlign: 'center' }}>
                <img src={charImage} alt={activeChar.name} style={{ width: '160px', height: '160px', borderRadius: '12px', border: `4px solid ${activeChar.accentColor}`, objectFit: 'cover', display: 'block' }} />
                <p style={{ fontSize: '11px', color: '#95a5a6', margin: '4px 0 0', fontWeight: 'bold' }}>{activeChar.name}</p>
              </div>
              {/* 右：吹き出し＋スコア */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {score !== null && (
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#e74c3c', lineHeight: 1 }}>
                    {score}<span style={{ fontSize: '14px', marginLeft: '2px' }}>点</span>
                  </div>
                )}
                {/* 吹き出し */}
                <div style={{ position: 'relative', background: '#f8f9fa', borderRadius: '14px', borderBottomLeftRadius: '4px', padding: '12px 14px', border: `2px solid ${activeChar.accentColor}22` }}>
                  <div style={{ position: 'absolute', bottom: '12px', left: '-10px', width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: `10px solid ${activeChar.accentColor}22` }} />
                  <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#2c3e50', fontWeight: 'bold' }}>
                    {shortComment || response.slice(0, 40) + (response.length > 40 ? '…' : '')}
                  </p>
                </div>
              </div>
            </div>
            {/* 詳細コメント */}
            <div style={{ borderTop: '1px dashed #eee', paddingTop: '14px' }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px', color: '#2c3e50' }}>{response}</p>
            </div>
          </div>
        </div>

        <footer style={{ marginTop: '40px', textAlign: 'center', fontSize: '11px', color: '#bdc3c7' }}>
          © Shiramine Yukito All rights reserved.
        </footer>
      </div>
    </div>
  );
}